import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, DataListItem, Input2FA } from '@node-space/ui-components'
import { useTwoFactorValidation } from 'hooks/useTwoFactorValidation'
import { MappedErrorMessageState } from 'types/errors'

interface RewardsReviewDataItem {
  label: string
  value: string
}

interface RewardsReviewBaseProps {
  data: RewardsReviewDataItem[]
  requestError: MappedErrorMessageState | null
  isPending: boolean
  onCancel: () => void
  require2FA?: boolean
  resetRequestError?: () => void
}

interface RewardsReviewWithout2FA extends RewardsReviewBaseProps {
  require2FA: false
  onConfirm: () => void
}
interface RewardsReviewWith2FA extends RewardsReviewBaseProps {
  require2FA: true
  onConfirm: (twoFactorCode?: string) => void
}

export type RewardsReviewProps = RewardsReviewWithout2FA | RewardsReviewWith2FA

export const RewardsReview = ({
  data,
  requestError,
  isPending,
  onCancel,
  onConfirm,
  require2FA = false,
  resetRequestError,
}: RewardsReviewProps) => {
  const { t } = useTranslation()
  const { twoFactorCode, validationError, handleCodeChange, validateCode } =
    useTwoFactorValidation()

  const handleConfirm = () => {
    if (require2FA && !validateCode()) {
      return
    }
    onConfirm(require2FA ? twoFactorCode : undefined)
  }

  const handleCodeChangeWithErrorReset = (value: string) => {
    handleCodeChange(value)
    if (require2FA && resetRequestError) {
      resetRequestError()
    }
  }

  const getInput2FAError = () => {
    if (validationError) {
      return validationError
    }

    const is403Error = requestError?.errorCode === 403
    if (is403Error) {
      return requestError.handledMessage as string
    }

    return undefined
  }

  const is403Error = requestError?.errorCode === 403
  const shouldShowCallout = requestError?.show && !(require2FA && is403Error)

  return (
    <Box flexCol gapY={16} width="full" paddingY={24}>
      {shouldShowCallout && (
        <Callout variant="error" testId="review-error-callout">
          {requestError?.handledMessage}
        </Callout>
      )}

      <Box flexCol>
        {data.map(item => (
          <DataListItem
            key={item.label}
            id={item.label}
            testId={`review-${item.label}`}
            title={t(item.label)}
            body={item.value}
          />
        ))}
      </Box>

      {require2FA && (
        <Box flexCol gapY={8}>
          <p className="text-body-regular text-foreground-prominent">{t('authCodeConfirm')}</p>
          <Input2FA
            testId="review-2fa-input"
            loading={isPending}
            error={getInput2FAError()}
            onChange={handleCodeChangeWithErrorReset}
          />
        </Box>
      )}

      <Box flex justify="between" gapX={12} paddingT={24}>
        <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          testId="confirm-button"
          disabled={isPending}
          loading={isPending}
          onClick={handleConfirm}
        >
          {t('confirm')}
        </Button>
      </Box>
    </Box>
  )
}
